import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";

export interface SalesUnitInput {
  id?: string;
  name: string;
  year: number;
  bf_target_mnok: number;
}

export function useUpsertSalesUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SalesUnitInput) => {
      const { id, ...rest } = input;
      if (id) {
        const { error } = await supabase.from("sales_units").update(rest).eq("id", id);
        if (error) throw error;
        return id;
      }
      const { data, error } = await supabase
        .from("sales_units")
        .insert(rest)
        .select("id")
        .single();
      if (error) throw error;
      return data.id;
    },
    onSuccess: async () => {
      // year droppes med vilje: readers bruker number-year. Bredt prefix
      // (["sales-units"]) matcher uansett type via React Query partial-match.
      await invalidateFor(qc, "sales_units");
    },
  });
}

export function useDeleteSalesUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      // Block deletion if the sales unit is referenced by any project_owners row.
      const { count, error: countErr } = await supabase
        .from("project_owners")
        .select("id", { count: "exact", head: true })
        .eq("sales_unit_id", id);
      if (countErr) throw countErr;
      if ((count ?? 0) > 0) {
        throw new Error(
          `Selgeren er brukt i ${count} prosjekt-eierskap. Kan ikke slette.`,
        );
      }

      // Remove members explicitly (CASCADE may handle it, but being explicit is safer).
      const { error: memErr } = await supabase
        .from("sales_unit_members")
        .delete()
        .eq("sales_unit_id", id);
      if (memErr) throw memErr;

      const { error } = await supabase.from("sales_units").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      // Sletting kan påvirke flere år (kun ett år treffes per delete, men vi
      // har ikke året i input). Bredt prefix.
      await invalidateFor(qc, "sales_units");
    },
  });
}

export function useAddSalesUnitMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { sales_unit_id: string; user_id: string }) => {
      const { error } = await supabase.from("sales_unit_members").insert(params);
      if (error) throw error;
    },
    onSuccess: async (_, params) => {
      await invalidateFor(qc, "sales_unit_members", {
        salesUnitId: params.sales_unit_id,
      });
    },
  });
}

export function useRemoveSalesUnitMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { sales_unit_id: string; user_id: string }) => {
      const { error } = await supabase
        .from("sales_unit_members")
        .delete()
        .eq("sales_unit_id", params.sales_unit_id)
        .eq("user_id", params.user_id);
      if (error) throw error;
    },
    onSuccess: async (_, params) => {
      await invalidateFor(qc, "sales_unit_members", {
        salesUnitId: params.sales_unit_id,
      });
    },
  });
}
